import type { RoomConfigRow, RoomConfigSortKey } from './logic.js';
import { BathIcon, BedIcons, OutlookIcon, WcIcon } from './RoomIcons.js';

// The rack's frozen left block — « Configuration hôtel » compressed into the
// unit column of the « Plan d'occupation ».
//
// WHY IT IS ONE ELEMENT AND NOT SIX COLUMNS. The rack is a CSS grid whose
// columns are DAYS; the unit column is a single sticky cell. Six sticky grid
// columns would each need their own `left` offset, kept in sync with six
// widths, in two places (header row and body row) — six chances to drift by a
// pixel and shear the header off its glyphs. Instead this is one sticky cell
// containing a flex row of fixed slots, and the header renders the SAME slots
// with the same widths. They cannot disagree, because they are the same markup.
//
// The glyphs are the Configuration components verbatim (`RoomIcons.tsx`), shrunk
// to the row by CSS alone: every one carries a viewBox, so setting a height is
// enough and the aspect ratio follows. One definition, two sizes — a second set
// of "small" icons would be a second thing to keep correct.

/** The slots, in the order the operator reads a room (Configuration's order:
 *  glyphs first, text confirms). `key` is what the header sorts on. */
const SLOTS: { cls: string; key: RoomConfigSortKey; label: string; title: string }[] = [
  { cls: 's-lits', key: 'bedsRank', label: 'Lits', title: 'Lits — king 180 · double 160 · simple ; ⇄ = convertible' },
  { cls: 's-vue', key: 'outlookKey', label: 'V', title: 'Vue — balcon (chambre SUP), sinon château, jardin, ville' },
  { cls: 's-bain', key: 'hasShower', label: 'B', title: 'Bain — douche à l’italienne ou baignoire' },
  { cls: 's-wc', key: 'wcNotSeparated', label: 'W', title: 'WC — séparé (pièce dédiée) ou non séparé' },
  { cls: 's-cat', key: 'categoryCode', label: 'Cat', title: 'Catégorie' },
  { cls: 's-no', key: 'roomNumber', label: 'N°', title: 'N° de chambre' },
];

export interface RackStripHeadProps {
  /** null while a named preset is applied — no column carries the arrow then,
   *  exactly as in Configuration, so the two can never both claim the sort. */
  sortKey: RoomConfigSortKey | null;
  sortDir: 'asc' | 'desc';
  onSort: (key: RoomConfigSortKey) => void;
}

export function RackStripHead({ sortKey, sortDir, onSort }: RackStripHeadProps) {
  return (
    <span className="pms-rack-label">
      <span className="pms-rack-strip is-head">
        {SLOTS.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`${s.cls} ${sortKey === s.key ? 'is-sorted' : ''}`}
            title={`${s.title} — cliquer pour trier`}
            onClick={() => onSort(s.key)}
          >
            {s.label}
            {sortKey === s.key && <i className="pms-rack-arrow">{sortDir === 'asc' ? '▲' : '▼'}</i>}
          </button>
        ))}
      </span>
    </span>
  );
}

export interface RackStripRowProps {
  row: RoomConfigRow;
  selected: boolean;
  onSelect: () => void;
}

export function RackStripRow({ row, selected, onSelect }: RackStripRowProps) {
  return (
    <span className="pms-rack-label">
      <button
        type="button"
        className={`pms-rack-strip ${selected ? 'is-selected' : ''}`}
        title={`${row.roomNumber}${row.categoryLabel === null ? '' : ` — ${row.categoryLabel}`}${
          row.remarks === null ? '' : ` · ${row.remarks}`
        }`}
        onClick={onSelect}
      >
        <span className="s-lits">
          <BedIcons layouts={row.bedLayouts} doubleBedType={row.doubleBedType} current={row.currentBedConfig} />
        </span>
        <span className="s-vue">
          <OutlookIcon kind={row.outlook} isSuperior={row.isSuperior} view={row.view} />
        </span>
        <span className="s-bain">
          <BathIcon hasShower={row.hasShower} />
        </span>
        <span className="s-wc">
          <WcIcon notSeparated={row.wcNotSeparated} />
        </span>
        {/* Tinted by capacity, like Configuration's banded columns — it is what
            makes a size legible as a block when the list is in number order and
            the categories are therefore interleaved. */}
        <span className={`s-cat ${row.maxOccupancy === null ? '' : `pms-occ is-${row.maxOccupancy}`}`}>
          {row.categoryCode ?? '—'}
        </span>
        <span className="s-no">{row.roomNumber}</span>
      </button>
    </span>
  );
}
